import { css } from 'aphrodite';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { isExhibitorUser } from 'redux/reducers/user';
import { checkIfTrue } from 'util/helpers';
import { style } from './style';

export const AgendaWidget: FC<{ agendaData: any; darkmode?: boolean; basic?: boolean }> = ({
  agendaData,
  darkmode = false,
  basic = false,
}) => {
  const styles = style({
    darkmode,
    basic,
    hasCalendarHeadline: !!agendaData.calendar_headline,
  });
  const history = useHistory();
  const cmsLinks = document.getElementsByClassName('cms_link');
  const isExhibitor = useSelector(isExhibitorUser);

  useEffect(() => {
    if (!cmsLinks) return;

    // Prevent cms injected links from redirecting as an external link if the route is inside the app
    function handleAppLink(event: any, route: string) {
      event.preventDefault();
      history.push(route);
    }

    Array.prototype.forEach.call(cmsLinks, (element: Element) => {
      const route = element.getAttribute('href');
      if (route && route.trim().charAt(0) === '/') {
        element.addEventListener('click', (event) => handleAppLink(event, route));
      }
    });

    return () =>
      Array.prototype.forEach.call(cmsLinks, (element: Element) => {
        const route = element.getAttribute('href');
        if (route && route.trim().charAt(0) === '/') {
          element.removeEventListener('click', (event) => handleAppLink(event, route));
        }
      });
  }, [cmsLinks, history]);

  const getTimeSlotInfo = (id: number, data: any): JSX.Element => {
    return (
      <div key={id} className={css(styles.row)}>
        {getTimeSlotHeader(data)}
        {getTimeSlotContent(data.content)}
      </div>
    );
  };

  const getTimeSlotHeader = (data: any): JSX.Element => {
    const type: string = data.type;
    const liveStreamBadge: JSX.Element = (
      <div className={css(styles.streamingBadge)}>{agendaData.streaming_live_bagde_text}</div>
    );
    const headerText: string = [
      type === 'ondemand' ? agendaData.on_demand_row_header_text : '',
      data.time_prefix_text,
      data.time,
    ].join(' ');
    return (
      <div className={css(styles.rowHeader)}>
        {headerText}
        {type === 'livestream' ? liveStreamBadge : null}
      </div>
    );
  };

  const getTimeSlotContent = (content: any[]): JSX.Element => {
    return (
      <div className={css(styles.rowContent)}>
        {content.map((contentElement: any, index: number) => {
          switch (contentElement.type) {
            case 'tech_talk_table':
              return getTechTalkTable(index, contentElement);
            case 'tech_talk_list':
              return getTechTalkList(index, contentElement);
            default:
              return getDefaultContent(index, contentElement);
          }
        })}
      </div>
    );
  };

  const getTechTalkTable = (id: number, content: any): JSX.Element => {
    const title: string = content.title;
    const rows: any[] = content.data;
    return (
      <div key={id} className={css(styles.table)}>
        {title ? <div className={css(styles.tableTitle)} dangerouslySetInnerHTML={{ __html: title }} /> : null}
        <div className={css(styles.tableContent, styles.tableStripedContent)}>
          {rows.map((row, index) => {
            return (
              <div key={index} className={css(styles.tableRow, styles.tableRowColumnSeparator)}>
                <div className={css(styles.tableCell)} style={{ width: '72%' }}>
                  <ul>
                    <li>{row.talk}</li>
                  </ul>
                </div>
                <div className={css(styles.tableCell, styles.presenterCell)} style={{ width: '28%' }}>
                  {row.presenter}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getTechTalkList = (id: number, content: any): JSX.Element => {
    const rows: any[] = content.data;
    return (
      <div key={id} className={css(styles.calendarCell)}>
        <div key={id} className={css(styles.table)}>
          <div className={css(styles.tableContent, styles.tableListContent)}>
            {rows.map((row, index) => {
              if (!row.talk) return null;
              return (
                <div key={index} className={css(styles.tableRow)}>
                  <div
                    className={css(styles.tableCell, styles.listTableCell)}
                    style={{ width: '72%' }}
                    dangerouslySetInnerHTML={{ __html: row.talk }}
                  />
                  <div
                    className={css(styles.tableCell, styles.listTableCell, styles.presenterCell)}
                    style={{ width: '28%' }}
                    dangerouslySetInnerHTML={{ __html: row.presenter }}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className={css(styles.separator)}></div>
      </div>
    );
  };

  const getDefaultContent = (id: number, content: any): JSX.Element => {
    return <div key={id} className={css(styles.defaultContent)} dangerouslySetInnerHTML={{ __html: content.data }} />;
  };

  return (
    <>
      <div className={css(styles.header)}>
        {agendaData.headline ? <h1 className={css(styles.headline)}>{agendaData.headline}</h1> : null}
        {agendaData.subheadline ? <p className={css(styles.subheadline)}>{agendaData.subheadline}</p> : null}
      </div>
      {agendaData.calendar_headline && (
        <div className={css(styles.calendarBannerHeadline)}>{agendaData.calendar_headline}</div>
      )}
      <div className={css(styles.calendar)}>
        {agendaData.calendar
          .filter((agendaEvent: any) => {
            return !isExhibitor || checkIfTrue(agendaEvent.display_to_exhibitors);
          })
          .map((timeSegmentData: any, index: number) => {
            return getTimeSlotInfo(index, timeSegmentData);
          })}
      </div>
    </>
  );
};
